import { type ReactElement } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Box } from '@mui/material'
import { FileDropzone } from '@ui2/forms'
import { FormControl } from '@ui/forms'

interface Props {
  name: string
  label: string
  /** Native input accept attribute; dropped files are validated by the schema. */
  accept: string
}

/**
 * The dropzone as a form field: holds a single file under `name`, so the
 * schema decides whether it is required and whether its type matches (see
 * fileOfType in ../../schema) -- a wrong file stays visible with its error
 * and blocks the submit instead of being silently cleared.
 */
const ModelFileField = ({ name, label, accept }: Props): ReactElement => {
  const { control, setValue, resetField } = useFormContext()
  const {
    field: { value },
    fieldState: { error },
  } = useController({ name, control })

  const handleFilesSelected = (files: File[]) => {
    setValue(name, files[0], { shouldValidate: true, shouldDirty: true })
  }

  return (
    <FormControl label={label} error={error?.message}>
      <FileDropzone
        accept={accept}
        title={value?.name}
        onFilesSelected={handleFilesSelected}
        hint={
          value ? (
            <Box
              component="span"
              onClick={event => {
                event.stopPropagation()
                resetField(name)
              }}
              sx={{ color: 'primary.main', fontWeight: 500, cursor: 'pointer' }}
            >
              Datei entfernen
            </Box>
          ) : undefined
        }
      />
    </FormControl>
  )
}

export default ModelFileField
